/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  UpcomingOffersService,
  type TUpcomingOffersFetchUpcomingOffersParams,
  type UpcomingOfferListV2
} from "@tivocorp/data-domain-lib";

import type { IUpcomingOffersInteractor } from "../../interfaces/IUpcomingOffersInteractor";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Shared, atomic interactor wrapping {@link UpcomingOffersService} for the
 * Content Detail screen's "Upcoming" section.
 *
 * Stateless: a fresh `UpcomingOffersService` is built per call via
 * {@link ServiceLocator}, and the {@link ApplicationFeatureArea} is fixed at
 * construction time so the `applicationFeatureArea` request header matches
 * the calling feature.
 */
export class UpcomingOffersInteractor
  extends BaseInteractor
  implements IUpcomingOffersInteractor
{
  private static readonly TAG: string = "UpcomingOffersInteractor";

  /**
   * @param mFeatureArea - ApplicationFeatureArea reported in the request
   *                       header for every call made by this instance.
   */
  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  /**
   * @inheritdoc
   */
  public async fetchUpcomingOffers(
    objectId: string,
    minEndTime: string,
    limit: number,
    offset: number,
    options?: TNetworkOptions
  ): Promise<UpcomingOfferListV2> {
    return this.executeGuarded(
      "fetchUpcomingOffers",
      async (guardedSignal: AbortSignal): Promise<UpcomingOfferListV2> => {
        Log.d(UpcomingOffersInteractor.TAG, "fetchUpcomingOffers() called");
        const service: UpcomingOffersService = ServiceLocator.getInstance(
          UpcomingOffersService,
          await this.buildServicesParams(
            TIVO_SERVICES.UpcomingOffersService,
            this.mFeatureArea
          )
        );
        const params: TUpcomingOffersFetchUpcomingOffersParams = {
          objectId,
          minEndTime,
          limit,
          offset
        };
        return service.fetchUpcomingOffers(params, guardedSignal);
      },
      options
    );
  }
}
